import { StockOrder } from "@towersystems/roam-common/lib/generated-types";
import React from "react";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";
import {
  EditStockOrderRootScreenParamList,
  StockOrderRootParamList,
} from "../../../../types";
import {
  CardStyleInterpolators,
  StackScreenProps,
} from "@react-navigation/stack";
import { PlaceholderLoadingScreen } from "../../../products/screens/PlaceholderLoadingScreen";
import { ResourcePageSkeleton } from "../../../point-of-sale/screens/ResourcPageSkeleton";
import { PageErrorOccurred } from "../../../../components";
import { QUERY_STOCK_ORDER } from "../../utils";
import { useLazyQuery } from "@apollo/client";
import {
  ModalCloseButton,
  createStackHeaderNavigator,
} from "@towersystems/roam-common-native";
import { EditStockOrderScreen } from "../EditStockOrderScreen";
import {
  ActiveStockOrderContext,
  useStockOrder,
} from "../../utils/active-stock-order";
import { AddOrderItemModalRootScreen } from "../AddOrderItemModalRootScreen";
import { EditOrderItemModalRootScreen } from "../EditOrderItemModalRootScreen";

export type EditStockOrderRootScreenProps = StackScreenProps<
  StockOrderRootParamList,
  "EditStockOrderRootScreen"
>;

const EditStockOrderRoot = ({
  stockOrder,
}: EditStockOrderRootScreenProps & {
  stockOrder: StockOrder;
}) => {
  const context = useStockOrder(stockOrder);

  const Drawer =
    createStackHeaderNavigator<EditStockOrderRootScreenParamList>();

  return (
    <ActiveStockOrderContext.Provider value={context}>
      <Drawer.Navigator
        screenOptions={{
          title: "",
          animationEnabled: true,
          headerLeft: () => <ModalCloseButton type="Back" />,
        }}
      >
        <Drawer.Group>
          <Drawer.Screen
            name="EditStockOrderScreen"
            component={EditStockOrderScreen}
          />
        </Drawer.Group>
        <Drawer.Group
          screenOptions={{
            headerShown: false,
            cardStyleInterpolator: CardStyleInterpolators.forHorizontalIOS,
          }}
        >
          <Drawer.Screen
            name="EditOrderItemModalRootScreen"
            component={EditOrderItemModalRootScreen}
          />
        </Drawer.Group>
        <Drawer.Group
          screenOptions={{
            headerShown: false,
            presentation: "modal",
          }}
        >
          <Drawer.Screen
            name="AddOrderItemModalRootScreen"
            component={AddOrderItemModalRootScreen}
          />
        </Drawer.Group>
      </Drawer.Navigator>
    </ActiveStockOrderContext.Provider>
  );
};

export const EditStockOrderRootScreen = (
  props: EditStockOrderRootScreenProps
) => {
  const [stockOrder, setStockOrder] = React.useState<StockOrder | undefined>(
    undefined
  );

  const [error, setError] = React.useState<string | undefined>(undefined);
  const transitionEnded = useHasTransitionEnded(props.navigation);

  const id = props.route.params.stockOrderId;
  const [q] = useLazyQuery(QUERY_STOCK_ORDER);

  React.useEffect(() => {
    if (id) {
      q({
        variables: { id },
      })
        .then((res) => {
          if (res.data) {
            setStockOrder(res.data.stockOrder);
          }
        })
        .catch((err) => {
          setError(err.message ?? "An error occurred");
        });
    }
  }, [id, q]);

  if (!stockOrder && error) {
    return (
      <PageErrorOccurred
        errorMessage={error}
        onRetry={() => {
          props.navigation.reset({
            index: 0,
            routes: [{ name: "StockOrderListScreen" }],
          });
        }}
      />
    );
  }

  // Wait for transition to end before rendering the screen
  // otherwise the screen animation will be janky
  if (!stockOrder || false == transitionEnded) {
    return (
      <PlaceholderLoadingScreen
        navigation={props.navigation}
        content={ResourcePageSkeleton}
      />
    );
  }

  return <EditStockOrderRoot stockOrder={stockOrder} {...props} />;
};
